import { useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import {
  type IxcRegistrationV1AccountCheck,
  IxcRegistrationV1CheckOutcome,
  IxcRegistrationV1ControlsMarginType,
  type IxcRegistrationV1ProfileCheck,
  type IxcRegistrationV1RegistrationAccountType,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getAccountCheckOutcome } from '../account/checks/account-check-helpers';
import { getProfileCheckOutcome } from '../profile/checks/profile-check-helpers';

export function useCreatableAccountRegistrationTypes(programId: string | undefined): IxcRegistrationV1RegistrationAccountType[] {
  const { data } = useGetProgram(programId ? { programId } : undefined);

  return useMemo(() => {
    if (!data?.program?.data?.controls?.registration?.settings?.canOpenAccounts) {
      return [];
    }

    return data.program.data.controls.registration.settings.accountTypes || [];
  }, [data?.program?.data?.controls]);
}

export const ORDERED_MARGIN_TYPES: IxcRegistrationV1ControlsMarginType[] = [
  IxcRegistrationV1ControlsMarginType.Cash,
  IxcRegistrationV1ControlsMarginType.Lpm,
];

export function useAllowedProgramAccountMarginTypes(programId: string | undefined): IxcRegistrationV1ControlsMarginType[] {
  const { data } = useGetProgram(programId ? { programId } : undefined);

  return useMemo(() => {
    if (!data?.program?.data?.controls?.registration?.settings?.marginType) {
      return [IxcRegistrationV1ControlsMarginType.Cash];
    }

    const marginTypeIndex = ORDERED_MARGIN_TYPES.indexOf(data.program.data.controls.registration.settings.marginType);

    if (marginTypeIndex === -1) {
      return [IxcRegistrationV1ControlsMarginType.Cash];
    }

    return ORDERED_MARGIN_TYPES.slice(0, marginTypeIndex + 1);
  }, [data?.program?.data?.controls]);
}

export function useAllowedAccountMarginTypes(accountId: string | undefined): IxcRegistrationV1ControlsMarginType[] {
  const { data } = useIxcRegistrationV1AccountQueryServiceAccountGet(accountId ? { accountId } : undefined);
  const programMarginTypes = useAllowedProgramAccountMarginTypes(data?.account?.programId);

  return useMemo(() => {
    if (!data?.account?.data?.controls?.registration?.settings?.marginType) {
      return [IxcRegistrationV1ControlsMarginType.Cash];
    }

    const marginTypeIndex = ORDERED_MARGIN_TYPES.indexOf(data.account.data.controls.registration.settings.marginType);

    if (marginTypeIndex === -1) {
      return [IxcRegistrationV1ControlsMarginType.Cash];
    }

    return programMarginTypes.slice(0, marginTypeIndex + 1) || [IxcRegistrationV1ControlsMarginType.Cash];
  }, [data?.account?.data?.controls, programMarginTypes]);
}

export function getCanActionProfileCheck(check: IxcRegistrationV1ProfileCheck, isClearingUser: boolean): boolean {
  const outcome = getProfileCheckOutcome(check);
  const needsAction = !outcome || outcome === IxcRegistrationV1CheckOutcome.Inconclusive;

  if (!needsAction) {
    return false;
  }

  return match(getOneOfType(check.checkType))
    .with(P.union('cipIdentity', 'cipOfac', 'cipIdentityMinimal', 'cipOfacMinimal'), () => true)
    .with('clearingManualProfileApproval', () => isClearingUser)
    .with(undefined, () => false)
    .exhaustive(() => isClearingUser);
}

export function getCanActionAccountCheck(check: IxcRegistrationV1AccountCheck, isClearingUser: boolean): boolean {
  const outcome = getAccountCheckOutcome(check);
  const needsAction = !outcome || outcome === IxcRegistrationV1CheckOutcome.Inconclusive;

  if (!needsAction) {
    return false;
  }

  return match(getOneOfType(check.checkType))
    .with(P.union('clearingManualApproval', 'clearingAutomatedApproval'), () => isClearingUser)
    .with('programApproval', () => true)
    .with(undefined, () => false)
    .exhaustive(() => isClearingUser);
}

export function useProgramRequiresSuitabilityQuestions(programId: string | undefined) {
  const { data } = useGetProgram(programId ? { programId } : undefined);

  if (!data?.program) {
    return true;
  }

  return !data.program.data.controls.registration?.settings?.optionalSuitabilityQuestions;
}
